import { useNavigate } from 'react-router-dom';
import type { Roadmap } from '../../services/achievements.service';

interface Props {
  open: boolean;
  roadmap: Roadmap | null;
  onClose: () => void;
  /** When true, shows the "Ver roadmap completo" footer link */
  showFullLink?: boolean;
}

export function RoadmapModal({ open, roadmap, onClose, showFullLink = true }: Props) {
  const navigate = useNavigate();

  if (!open || !roadmap) return null;

  const nextSteps = roadmap.steps.filter((s) => !s.assignment?.completedAt).slice(0, 3);
  const completedInTier = roadmap.totalSteps - roadmap.remainingSteps;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-lg rounded-2xl bg-surface-container-low border border-outline-variant/20 shadow-2xl overflow-hidden roadmap-pop">
        <button
          aria-label="Cerrar"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-lg text-on-surface-variant hover:text-on-surface hover:bg-surface-container"
        >
          <span className="material-symbols-outlined text-[18px] leading-8">close</span>
        </button>

        <div className="px-6 pt-7 pb-5 text-center border-b border-outline-variant/10">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-2">
            Roadmap — Tier {roadmap.currentTier}
          </div>
          <h2 className="text-xl font-bold text-on-surface">
            {nextSteps.length === 0 ? '¡Tier completado!' : 'Tus próximos pasos'}
          </h2>
          <p className="text-sm text-on-surface-variant mt-1">
            {completedInTier} / {roadmap.totalSteps} completados en este tier
          </p>
          <div className="mt-3 w-full h-2 bg-surface-container rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-yellow-400 transition-all"
              style={{
                width: `${roadmap.totalSteps === 0 ? 0 : (completedInTier / roadmap.totalSteps) * 100}%`,
              }}
            />
          </div>
        </div>

        <div className="px-6 py-5 space-y-3">
          {nextSteps.length === 0 ? (
            <p className="text-center text-sm text-on-surface-variant py-6">
              Esperando al check del mentor para desbloquear el siguiente tier.
            </p>
          ) : (
            nextSteps.map((step, idx) => (
              <div
                key={step.achievement.id}
                className="flex items-start gap-3 p-3 rounded-xl bg-surface-container/60 border border-outline-variant/10 roadmap-step"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center font-bold text-sm shrink-0">
                  {step.position}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-on-surface text-sm">{step.achievement.title}</div>
                  <p className="text-xs text-on-surface-variant mt-0.5">{step.achievement.description}</p>
                  {step.progressPercent > 0 && step.progressPercent < 100 && (
                    <div className="mt-2 w-full h-1 bg-surface-container rounded-full overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${step.progressPercent}%` }} />
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-6 pb-6 pt-2 flex items-center justify-between gap-3">
          {showFullLink ? (
            <button
              onClick={() => {
                onClose();
                navigate('/achievements');
              }}
              className="text-sm text-primary hover:underline"
            >
              Ver roadmap completo →
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold hover:opacity-90"
          >
            Entendido
          </button>
        </div>

        <style>{`
          @keyframes roadmap-pop-in {
            0% { transform: scale(0.95) translateY(12px); opacity: 0; }
            100% { transform: scale(1) translateY(0); opacity: 1; }
          }
          .roadmap-pop {
            animation: roadmap-pop-in 0.35s ease-out both;
          }
          @keyframes step-fade-in {
            from { opacity: 0; transform: translateX(-8px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .roadmap-step {
            animation: step-fade-in 0.35s ease-out both;
          }
        `}</style>
      </div>
    </div>
  );
}
